<template>
  <div class="ac-strategy">
    <div class="ac-strategy__toolbar d-flex flex-wrap align-center ga-3 mb-2">
      <v-select
        v-model="selectedElink"
        :items="elinkSelectItems"
        item-title="title"
        item-value="value"
        label="选择 eLink"
        variant="outlined"
        density="comfortable"
        hide-details
        rounded="lg"
        class="ac-strategy__elink-select"
      />
    </div>
    <p class="text-caption text-medium-emphasis mb-4">
      根据所选 eLink 维护 eLink 空调参数与 eBlock 空调参数；支持当前数据、上报数据、策略对比及同步/设置/历史。
    </p>

    <AcStrategySection
      title="eLink空调参数"
      title-icon="mdi-access-point"
      :sync-status="elink.syncStatus"
      :report-time="elink.reportTime"
      :blocks="elink.blocks"
      :last-report-snapshot="elink.lastReportSnapshot"
      @sync="onSync('elink')"
      @settings="openDialog('elink')"
      @history="openHistory('elink')"
    />

    <AcStrategySection
      title="eBlock空调参数"
      title-icon="mdi-cube-outline"
      class="mt-4"
      :sync-status="eblock.syncStatus"
      :report-time="eblock.reportTime"
      :blocks="eblock.blocks"
      :last-report-snapshot="eblock.lastReportSnapshot"
      @sync="onSync('eblock')"
      @settings="openDialog('eblock')"
      @history="openHistory('eblock')"
    />

    <AirConditioningParamDialog
      v-model="dialogOpen"
      :kind="dialogKind"
      :initial-blocks="dialogInitialBlocks"
      @save="onDialogSave"
    />

    <v-dialog v-model="historyDialogOpen" max-width="520" scrollable>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center">
          <span>空调参数设置历史</span>
          <v-spacer />
          <v-btn icon variant="text" density="comfortable" @click="historyDialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="text-wrap pb-0">
          {{ historySubtitle }}
        </v-card-subtitle>
        <v-card-text class="pt-4">
          <v-list v-if="historyItems.length" lines="two" class="pa-0 rounded-lg ac-history-list">
            <v-list-item v-for="(it, i) in historyItems" :key="i" border rounded class="mb-2">
              <v-list-item-title class="text-body-2">{{ it.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ it.sub }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
          <div v-else class="text-medium-emphasis text-body-2 py-8 text-center">暂无历史记录</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { eui } from 'ems-ui'
import AirConditioningParamDialog from './AirConditioningParamDialog.vue'
import AcStrategySection from './AcStrategySection.vue'

const elinkSelectItems = [
  { title: 'eLink', value: 'elink-root' },
  { title: '1-1#eLink', value: '1-1' },
  { title: '1-2#eLink', value: '1-2' },
]

const selectedElink = ref('1-1')

function createSectionState() {
  return {
    syncStatus: 'unsynced',
    reportTime: null,
    blocks: [],
    lastReportSnapshot: null,
  }
}

const eblock = ref(createSectionState())
const elink = ref(createSectionState())

const dialogOpen = ref(false)
const dialogKind = ref('eblock')

const dialogInitialBlocks = computed(() =>
  dialogKind.value === 'eblock' ? eblock.value.blocks : elink.value.blocks,
)

const historyDialogOpen = ref(false)
const historySection = ref('elink')

const historySubtitle = computed(() => {
  const el = elinkSelectItems.find((x) => x.value === selectedElink.value)
  const name = el?.title ?? selectedElink.value
  const sec = historySection.value === 'eblock' ? 'eBlock' : 'eLink'
  return `${name} · ${sec} 空调参数（演示数据）`
})

/** 演示：与所选 eLink、当前操作区块联动文案 */
const historyItems = computed(() => {
  const el = elinkSelectItems.find((x) => x.value === selectedElink.value)
  const name = el?.title ?? selectedElink.value
  const sec = historySection.value === 'eblock' ? 'eBlock' : 'eLink'
  return [
    {
      title: `${name} ${sec} 参数已保存（本地草稿）`,
      sub: '2026-05-10 14:32:01 · 操作人：系统演示',
    },
    {
      title: `${name} ${sec} 策略同步成功`,
      sub: '2026-05-09 09:15:22 · 网关 ACK',
    },
    {
      title: `${name} ${sec} 打开参数设置对话框`,
      sub: '2026-05-08 16:40:00',
    },
  ]
})

function openDialog(kind) {
  dialogKind.value = kind
  dialogOpen.value = true
}

function onDialogSave(blocks) {
  const target = dialogKind.value === 'eblock' ? eblock : elink
  target.value = {
    ...target.value,
    blocks,
    syncStatus: 'unsynced',
    reportTime: null,
    lastReportSnapshot: null,
  }
  eui.showToast('参数已保存到本地草稿', 'success')
}

function onSync(section) {
  const target = section === 'eblock' ? eblock : elink
  if (!target.value.blocks?.length) {
    eui.showToast('请先完成「空调策略设置」并保存参数', 'info')
    return
  }
  const snapshot = JSON.stringify(target.value.blocks, null, 2)
  const now = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  target.value = {
    ...target.value,
    syncStatus: 'synced',
    reportTime: ts,
    lastReportSnapshot: snapshot,
  }
  eui.showToast('演示：空调策略同步成功', 'success')
}

function openHistory(section) {
  historySection.value = section
  historyDialogOpen.value = true
}

defineExpose({
  getDraft: () => ({
    module: 'air_conditioning',
    selectedElink: selectedElink.value,
    eblock: {
      syncStatus: eblock.value.syncStatus,
      reportTime: eblock.value.reportTime,
      blocks: eblock.value.blocks,
      lastReportSnapshot: eblock.value.lastReportSnapshot,
    },
    elink: {
      syncStatus: elink.value.syncStatus,
      reportTime: elink.value.reportTime,
      blocks: elink.value.blocks,
      lastReportSnapshot: elink.value.lastReportSnapshot,
    },
  }),
})
</script>

<style scoped lang="scss">
.ac-strategy {
  max-width: 960px;
}

.ac-strategy__elink-select {
  min-width: 220px;
  max-width: 320px;
}

.ac-history-list {
  background: transparent;
}
</style>
